'use client';

import SuperCluster from 'supercluster';
import { Entity, ClusterPoint, ClusterOptions, Feature } from '../types';
import { determineClusterType } from '../components/ClusterMarker';

/**
 * Default clustering options
 */
export const DEFAULT_CLUSTER_OPTIONS: ClusterOptions = {
  radius: 60,
  maxZoom: 15,
  minZoom: 0,
  minPoints: 2,
  extent: 512,
  nodeSize: 64, // ADD: For better performance with nested clusters
};

/**
 * Zoom level thresholds for clustering behavior
 */
export const ZOOM_THRESHOLDS = {
  CLUSTER_ALL: 14,    // CHANGED: From 12
  INDIVIDUAL_ALL: 15, // CHANGED: From 15 to allow more cluster levels
  TRANSITION_START: 14,
  TRANSITION_END: 18,
};
/**
 * Convert entities to GeoJSON points for clustering
 */
export const entitiesToGeoJSON = (entities: Entity[]): ClusterPoint[] => {
  return entities
    .filter(entity => {
      // Only include entities with valid coordinates
      if (!entity.addresses || entity.addresses.length === 0) return false;
      
      const address = entity.addresses[0];
      if (!address.gps_latitude || !address.gps_longitude) return false;
      
      const lat = parseFloat(address.gps_latitude);
      const lng = parseFloat(address.gps_longitude);
      
      return !isNaN(lat) && !isNaN(lng) && 
             lat >= -90 && lat <= 90 && 
             lng >= -180 && lng <= 180;
    })
    .map(entity => {
      const address = entity.addresses![0];
      const lat = parseFloat(address.gps_latitude!);
      const lng = parseFloat(address.gps_longitude!);
      
      return {
        type: 'Feature' as const,
        properties: {
          cluster: false,
          entity,
          entityId: entity.id,
          name: entity.name || 'Unknown',
          // Convert feature IDs to strings
          features: entity.features?.map(f => String(f.id)) || [],
          featureDescriptions: entity.features?.map(f => f.desc || '') || [],
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [lng, lat] as [number, number],
        },
      };
    });
};
/**
 * Create and configure SuperCluster instance
 */
export const createSuperCluster = (options: Partial<ClusterOptions> = {}): SuperCluster => {
  const config = { ...DEFAULT_CLUSTER_OPTIONS, ...options };
  
  return new SuperCluster({
    radius: config.radius,
    maxZoom: config.maxZoom,
    minZoom: config.minZoom,
    minPoints: config.minPoints,
    extent: config.extent,
    
    // Custom reduce function to aggregate cluster properties
    reduce: (accumulated: any, properties: any) => {
      // Collect all features from entities in this cluster
      if (properties.features) {
        accumulated.features = accumulated.features || [];
        // Ensure features are strings
        const features = properties.features.map((f: any) => String(f));
        accumulated.features.push(...features);
      }
      
      // Collect all feature descriptions for color coding
      if (properties.featureDescriptions) {
        accumulated.featureDescriptions = accumulated.featureDescriptions || [];
        const descriptions = Array.isArray(properties.featureDescriptions) 
          ? properties.featureDescriptions.flat() 
          : [properties.featureDescriptions];
        accumulated.featureDescriptions.push(...descriptions);
      }
      
      // Keep track of entity types for cluster color coding
      if (properties.entity) {
        accumulated.entities = accumulated.entities || [];
        accumulated.entities.push(properties.entity);
      }
    },
    
    // Map function to process cluster properties
    map: (properties: any) => {
      return {
        entity: properties.entity,
        entityId: properties.entityId,
        name: properties.name || 'Unknown',
        // Ensure features are strings
        features: properties.features?.map((f: any) => String(f)) || [],
        featureDescriptions: properties.featureDescriptions || [],
      };
    },
  });
};

/**
 * Get clusters and individual points for a given zoom level and bounds
 */
export const getClustersInBounds = (
  supercluster: SuperCluster,
  bounds: [number, number, number, number],
  zoom: number
): ClusterPoint[] => {
  try {
    if (!supercluster || typeof supercluster.getClusters !== 'function') {
      console.warn('🗺️ SuperCluster not properly initialized');
      return [];
    }
    
    if (!(supercluster as any).trees || Object.keys((supercluster as any).trees).length === 0) {
      console.warn('🗺️ SuperCluster has no data loaded yet');
      return [];
    }
    
    const clusters = supercluster.getClusters(bounds, Math.floor(zoom));
    
    return clusters.map((cluster: any) => {
      if (cluster.properties.cluster) {
        // It's a cluster
        const clusterId = cluster.properties.cluster_id;
        const pointCount = cluster.properties.point_count;
        
        // Determine cluster type based on aggregated feature descriptions
        const clusterType = determineClusterType(cluster.properties.featureDescriptions || []);
        
        return {
          type: 'Feature' as const,
          properties: {
            cluster: true,
            clusterId,
            point_count: pointCount,
            point_count_abbreviated: pointCount >= 1000 ? 
              `${Math.floor(pointCount / 1000)}k+` : 
              pointCount.toString(),
            features: cluster.properties.features,
            featureDescriptions: cluster.properties.featureDescriptions,
            clusterType,
            name: `Cluster of ${pointCount}`, // ADD THIS LINE
            entityId: 0, // ADD THIS LINE (required by type, use 0 for clusters)
          },
          geometry: cluster.geometry,
        };
      } else {
        // It's an individual point
        return {
          type: 'Feature' as const,
          properties: {
            cluster: false,
            entity: cluster.properties.entity,
            entityId: cluster.properties.entityId,
            name: cluster.properties.entity?.name || 'Unknown', // ADD THIS LINE
            features: cluster.properties.features,
            featureDescriptions: cluster.properties.featureDescriptions,
          },
          geometry: cluster.geometry,
        };
      }
    });
  } catch (error) {
    console.error('Error getting clusters in bounds:', error);
    return [];
  }
};


/**
 * Get the children of a cluster (for expansion)
 */
export const getClusterChildren = (
  supercluster: SuperCluster,
  clusterId: string | number
): ClusterPoint[] => {
  try {
    const children = supercluster.getChildren(Number(clusterId));
    
    return children.map((child: any) => ({
      type: 'Feature' as const,
      properties: {
        cluster: child.properties.cluster || false,
        clusterId: child.properties.cluster_id,
        point_count: child.properties.point_count,
        entity: child.properties.entity,
        entityId: child.properties.entityId || 0, // Provide default
        name: child.properties.entity?.name || `Cluster of ${child.properties.point_count || 1}`, // ADD THIS LINE
        features: child.properties.features,
      },
      geometry: child.geometry,
    }));
  } catch (error) {
    console.error('Error getting cluster children:', error);
    return [];
  }
};

/**
 * Get the zoom level needed to expand a cluster
 */
export const getClusterExpansionZoom = (
  supercluster: SuperCluster,
  clusterId: string | number
): number => {
  try {
    // Ensure clusterId is a number
    const id = typeof clusterId === 'string' ? parseInt(clusterId, 10) : clusterId;
    const expansionZoom = supercluster.getClusterExpansionZoom(id);
    
    // Add a small buffer to ensure we're past the threshold
    const zoom = supercluster.getClusterExpansionZoom(id);
    return zoom > 16 ? 16 : zoom; // Cap at 16 to force individual markers
  } catch (error) {
    console.error('Error getting cluster expansion zoom:', error);
    return ZOOM_THRESHOLDS.INDIVIDUAL_ALL;
  }
};


/**
 * Determine if clustering should be active at a given zoom level
 */
export const shouldCluster = (zoom: number): boolean => {
  return zoom < ZOOM_THRESHOLDS.INDIVIDUAL_ALL;
};

/**
 * Get appropriate cluster radius based on zoom level
 */
export const getClusterRadius = (zoom: number): number => {
  // More gradual radius reduction for multi-level clustering
  if (zoom >= 18) return 30;
  if (zoom >= 16) return 40;
  if (zoom >= 14) return 50;
  if (zoom >= 12) return 60;
  if (zoom >= 10) return 70;
  if (zoom >= 8) return 80;
  if (zoom >= 6) return 90;
  return 100;
};
/**
 * Convert Google Maps bounds to SuperCluster bounds format
 */
export const googleBoundsToClusterBounds = (
  bounds: google.maps.LatLngBounds
): [number, number, number, number] => {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  
  return [
    sw.lng(), // west
    sw.lat(), // south
    ne.lng(), // east
    ne.lat()  // north
  ];
};

/**
 * Get bounds that encompass all points in a cluster
 */
export const getClusterBounds = (
  supercluster: SuperCluster,
  clusterId: string | number
): google.maps.LatLngBounds | null => {
  try {
    const leaves = supercluster.getLeaves(Number(clusterId), Infinity);
    
    if (!leaves || leaves.length === 0) return null;
    
    const bounds = new google.maps.LatLngBounds();
    
    leaves.forEach((leaf: any) => {
      const [lng, lat] = leaf.geometry.coordinates;
      bounds.extend(new google.maps.LatLng(lat, lng));
    });
    
    return bounds;
  } catch (error) {
    console.error('Error getting cluster bounds:', error);
    return null;
  }
};

/**
 * Animation helper for smooth transitions between cluster states
 */
export const animateClusterTransition = (
  element: HTMLElement,
  fromState: 'cluster' | 'individual',
  toState: 'cluster' | 'individual',
  duration: number = 300
): Promise<void> => {
  return new Promise((resolve) => {
    if (!element) {
      resolve();
      return;
    }
    
    const startTime = performance.now();
    
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Use easing function for smooth animation
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      if (fromState === 'individual' && toState === 'cluster') {
        // Animate individual markers merging into cluster
        const scale = 1 - (easeProgress * 0.3); // Scale down slightly
        const opacity = 1 - (easeProgress * 0.2); // Fade slightly
        
        element.style.transform = `scale(${scale})`;
        element.style.opacity = opacity.toString();
      } else if (fromState === 'cluster' && toState === 'individual') {
        // Animate cluster expanding to individual markers
        const scale = 1 + (easeProgress * 0.2); // Scale up slightly
        const opacity = 0.8 + (easeProgress * 0.2); // Fade in
        
        element.style.transform = `scale(${scale})`;
        element.style.opacity = opacity.toString();
      }
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Reset styles
        element.style.transform = '';
        element.style.opacity = '';
        resolve();
      }
    };
    
    requestAnimationFrame(animate);
  });
};

/**
 * Utility to batch marker updates for performance
 */
export class MarkerUpdateBatcher {
  private updates: Map<string, () => void> = new Map();
  private timeoutId: number | null = null;
  
  schedule(markerId: string, updateFn: () => void) {
    this.updates.set(markerId, updateFn);
    
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    
    this.timeoutId = window.setTimeout(() => {
      this.flush();
    }, 16); // ~60fps
  }
  
  flush() {
    this.updates.forEach((updateFn) => {
      try {
        updateFn();
      } catch (error) {
        console.error('Error in batched marker update:', error);
      }
    });
    
    this.updates.clear();
    this.timeoutId = null;
  }
  
  clear() {
    this.updates.clear();
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}